// docs/22 O4/B2 — the opening's two cinematics over the encounter.
//
// FACE-OFF (once per encounter per browser, as the first battle opens): letterbox bars slide in,
// the board camera eases in on the leader, the boss card names them, the story's parley plays line
// by line in dialogue boxes (the leader's with their portrait), the leader sounds the battle cry,
// then the bars slide away and the objective card ("The first battle of …") stands over the board.
// The first turn's clock is held server-side while the players read (combat::hold_opening, docs/153
// OE7): this browser says it has read (`opening_read`) as the bars slide away, and says it again while
// the turn is still held. Every line auto-advances after a reading beat and Skip / Esc / Space / a
// click outside the box ends it at once. A Lock plan counts as read on the server, so the hold never
// refuses a plan.
//
// ESCAPE (the leader never dies here: the sim drops them and the scene plays their getaway): when the
// opening ends in victory, the route waits on this card — letterbox, the getaway as a narrator's
// title card, the leader's vow with their portrait, then "A named foe is born" with their name and
// title. A getaway mid-fight (the Beast's brood fights on) gets only a bark with the vow (EncounterHud).
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, CINE_TYPE_GAIN, DialogueBox, HotkeyKbd, Portrait, Reveal, SightLayer, useBeatScope, useBeats, useReducedMotion } from '../components';
import { useEncounter } from '../../core/encounter/session';
import { rpc } from '../../core/net/reducers';
import { usePlayback } from '../../core/stores/playback';
import { useCinematic } from '../../core/stores/cinematic';
import { FATE, useEncounterSecured, useFoeFate, useOpeningStory, usePauseState, usePulse } from '../../core/subscriptions/hooks';
import { useLinkUp } from '../../core/stores/link';
import { getawayOf } from './NamedFoe';
import { display, withHeroes, type Genesis } from '../../core/story/genesis';
import { beatsFor } from '../../core/story/cues';
import { useMusicMood } from '../../core/audio/music';
import { playSfx, preloadSfx } from '../../core/audio';
import { BORN_MS, NAME_MS, OBJECTIVE_MS, faceoffSteps, leaderUnitOf, readMs } from './openingSteps';
import { hotkeyFor } from '../../core/input/hotkeys';
import './opening.css';

const CINE_KEYS = ['cine.skip', 'cine.next'] as const;

const ENC_ENDED = 3;
/** How often a browser that has read the opening says so again while the first turn is still held. */
const READ_RETRY_MS = 4000;
/** How long a held first turn waits for this browser's opening story to load before it has nothing to read. */
const NO_STORY_MS = 4000;
/** Encounter outcomes (fk_sim) after which the leader is off the board: Victory, Surrendered. */
const WON = new Set([0, 3]);

/** Esc (skip) and Space (step on) end or step a cinematic before the encounter's keyboard grammar sees them (hotkeys, docs/87). */
function useCinematicKeys(active: boolean, onKey: (key: 'cine.skip' | 'cine.next') => void): void {
  useEffect(() => {
    if (!active) return;
    const h = (e: KeyboardEvent) => {
      const key = hotkeyFor(e, CINE_KEYS);
      if (!key) return;
      e.preventDefault(); e.stopImmediatePropagation();
      onKey(key);
    };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [active, onKey]);
}

// ---- the director: tells the scene who the named foe is ----------------------------------------------

/**
 * Publishes the encounter's named foe (name + how they get away) for the scene's playback: the
 * opening's from the story; any other's once their plot armour has held (docs/77 NF3).
 */
export function OpeningDirector() {
  const encounterId = useEncounter((s) => s.encounterId);
  useEffect(() => { void preloadSfx([...MOMENTS], true); }, []);
  const g = useOpeningStory(encounterId);
  const setNamedFoe = useCinematic((s) => s.setNamedFoe);
  const setStory = useCinematic((s) => s.setStory);
  useEffect(() => { setStory(g); return () => setStory(undefined); }, [g, setStory]);
  const escape = g?.battle.named_foe?.escape;
  const name = g?.battle.leader.name;
  const fate = useFoeFate(encounterId);
  const away = fate?.fate === FATE.ESCAPED ? fate : undefined;
  useEffect(() => {
    if (encounterId !== undefined && name && escape) setNamedFoe({ encounterId, name, escape });
    else if (encounterId !== undefined && away) setNamedFoe({ encounterId, name: away.name, escape: getawayOf(away.npc) });
    else setNamedFoe(undefined);
  }, [encounterId, name, escape, away, setNamedFoe]);
  useEffect(() => () => setNamedFoe(undefined), [setNamedFoe]);
  return null;
}

// ---- face-off -------------------------------------------------------------------------------------

/**
 * The face-off as a pure view (the live HUD and the `?faceoff=1` harness both mount it).
 * `onFocus(true)` while the camera should frame the leader; `onDone` when it is over or skipped.
 */
export function FaceoffView({ g, heroes, leaderFrame, onFocus, onRead, onDone, stage }: {
  g: Genesis; heroes: string[]; leaderFrame?: number; onFocus?: (on: boolean) => void;
  /** The cinematic part is over (the objective card is up, or it was skipped): nothing more to read. */
  onRead?: () => void;
  onDone: () => void;
  /** Where the objective card sits (the HUD's board stage); without it the card is portalled with the rest. */
  stage?: boolean;
}) {
  const steps = useMemo(() => faceoffSteps(g, heroes), [g, heroes]);
  const [i, setI] = useState(0);
  const [typed, setTyped] = useState(-1);
  const step = steps[Math.min(i, steps.length - 1)]!;
  const cinematic = step.kind !== 'objective';
  const next = useCallback(() => setI((k) => k + 1), []);
  const skip = useCallback(() => setI((k) => Math.max(k, steps.length - 1)), [steps.length]);
  useEffect(() => { if (i >= steps.length) onDone(); }, [i, steps.length, onDone]);
  useEffect(() => { if (!cinematic || i >= steps.length) onRead?.(); }, [cinematic, i, steps.length, onRead]);
  useEffect(() => { onFocus?.(cinematic); }, [cinematic, onFocus]);
  useEffect(() => () => onFocus?.(false), [onFocus]);
  // the beats: the name card and the objective hold for a fixed time, a line once it is typed
  useEffect(() => {
    if (step.kind === 'line' && typed !== i) return;
    const ms = step.kind === 'name' ? NAME_MS : step.kind === 'objective' ? OBJECTIVE_MS : readMs(step.line.text);
    const t = setTimeout(next, ms);
    return () => clearTimeout(t);
  }, [step, typed, i, next]);
  useCinematicKeys(cinematic, useCallback(() => skip(), [skip]));
  // docs/43: a parley line's beats (or the battle cry's) land as its words appear
  const [typedN, setTypedN] = useState(0);
  useEffect(() => { setTypedN(0); }, [i]);
  useBeatScope(i);
  useMusicMood(cinematic ? 'tension' : undefined, cinematic);
  // docs/44: each step's moment — the name rises and slams down, a line slides in, the cry (a warband's
  // horn, raiders' strike, a beast's roar) with the goblins' own yell, the objective's drum
  useEffect(() => {
    if (i >= steps.length) return;
    if (step.kind === 'name') { playSfx('cine.rise', { gain: 0.8 }); playSfx('cine.name', { delayMs: 650 }); }
    else if (step.kind === 'objective') playSfx('cine.objective');
    else if (!step.cry) playSfx('cine.line');
    else {
      playSfx('cine.cry', { band: CRY_BAND[g.battle.foe] ?? 1 });
      if (g.battle.foe !== 'Beast') playSfx('voice.small_attack', { delayMs: 250 });
    }
  }, [i]);
  const beats = useMemo(() => (step.kind !== 'line' ? [] : beatsFor(g, step.cry ? 'Cry' : 'Parley', step.cry ? 0 : i - 1, step.line.text)), [g, step, i]);
  useBeats(beats, typedN);
  if (i >= steps.length) return null;

  const realm = display(g.realm.name);
  const objective = (
    <div className="fk-hud__opening fk-hit" role="status" data-testid="opening-call" onClick={next}>
      <Reveal kind="pop" className="fk-hud__opening-card">
        <span className="fk-hud__opening-eyebrow">The first battle of {realm}</span>
        <span className="fk-hud__opening-title">{g.battle.title}</span>
        <span className="fk-hud__opening-objective">{g.battle.objective}</span>
      </Reveal>
    </div>
  );
  if (!cinematic) return stage ? objective : createPortal(<div className="fk-faceoff fk-faceoff--out" data-step="objective">{objective}</div>, document.body);

  return createPortal(
    <div className="fk-faceoff" role="dialog" aria-modal="false" aria-label={`${display(g.battle.leader.name)} faces the fellowship`} data-testid="opening-faceoff" data-step={step.kind} data-index={i} onClick={skip}>
      <SightLayer fixed />
      <div className="fk-cine__bar fk-cine__bar--top">
        <span className="fk-cine__eyebrow">The first battle of {realm} · {g.battle.title}</span>
        <span className="fk-cine__clock">the turn clock waits until you have read this</span>
        <Button small variant="ghost" className="fk-cine__skip" onClick={(e) => { e.stopPropagation(); skip(); }} data-testid="faceoff-skip">Skip <span aria-hidden="true">▸</span> <HotkeyKbd id="cine.skip" /></Button>
      </div>
      <div className="fk-cine__bar fk-cine__bar--bottom" />
      {step.kind === 'name' && (
        <div className="fk-faceoff__boss" key="name" data-testid="faceoff-name">
          <span className="fk-faceoff__rule" aria-hidden="true" />
          <span className="fk-faceoff__boss-name">{display(g.battle.leader.name)}</span>
          <span className="fk-faceoff__boss-title">{g.battle.leader.title}</span>
          <span className="fk-faceoff__rule" aria-hidden="true" />
        </div>
      )}
      {step.kind === 'line' && (
        <div className="fk-cine__line" onClick={(e) => e.stopPropagation()}>
          <DialogueBox key={i} data-testid="faceoff-line" className={`fk-cine__dialogue ${step.cry ? 'fk-cine__dialogue--cry' : ''}`} cps={60} typeGain={CINE_TYPE_GAIN}
            speaker={step.line.speaker ? <>{display(step.line.speaker)}{step.cry && <span className="fk-cine__tag"> · battle cry</span>}</> : undefined}
            portrait={step.leader ? <Portrait frame={leaderFrame} scale={3} title={g.battle.leader.name} /> : undefined}
            text={step.line.text} more onDone={() => setTyped(i)} onAdvance={next} onType={setTypedN} />
        </div>
      )}
    </div>,
    document.body,
  );
}

/** The live face-off: the first time this browser sees the opening's first pulse. */
export function OpeningFaceoff() {
  const encounterId = useEncounter((s) => s.encounterId);
  const g = useOpeningStory(encounterId);
  const snapshot = useEncounter((s) => s.snapshot);
  const ready = useEncounter((s) => !!s.boardSnapshot);
  const state = useEncounter((s) => s.state);
  const heroNames = useEncounter((s) => s.heroNames);
  const seen = useCinematic((s) => s.faceoffSeen);
  const markSeen = useCinematic((s) => s.setFaceoffSeen);
  const setFocus = useCinematic((s) => s.setFocus);
  const [run, setRun] = useState<bigint>();
  // the encounter this browser has finished reading the opening of (the faceoff seen, skipped or
  // never coming), so the first turn's clock may run for it
  const [read, setRead] = useState<bigint>();
  const pulse = snapshot?.pulseIndex ?? 0;
  useEffect(() => {
    if (!g || encounterId === undefined || !ready || state === ENC_ENDED || pulse > 1 || run === encounterId) return;
    if (seen(encounterId)) { setRead(encounterId); return; }
    markSeen(encounterId); setRun(encounterId);
  }, [g, encounterId, ready, state, pulse, seen, markSeen, run]);
  // docs/153 OE7: the first turn is held while the players read (a pause that names no one). Once this
  // browser has read, it says so (combat::opening_read, idempotent), and again while the hold stands:
  // a call lost to a dropped link or a slow subscription must not leave the table waiting on the cap.
  const pause = usePauseState(encounterId);
  const held = !!pause?.paused && !pause.pausedBy;
  // the turn the server is on (the board's snapshot lags it while a turn plays back)
  const turn = usePulse(encounterId)?.index ?? pulse;
  const up = useLinkUp();
  useEffect(() => {
    // the first turn only: a later turn's hold waits for its playback (TurnHold.tsx, docs/155 TC2)
    if (encounterId === undefined || read !== encounterId || !held || !up || turn > 1) return;
    void rpc.openingRead(encounterId);
    const t = setInterval(() => void rpc.openingRead(encounterId), READ_RETRY_MS);
    return () => clearInterval(t);
  }, [encounterId, read, held, up, turn]);
  // a held turn whose opening story never reaches this browser has nothing here to read
  useEffect(() => {
    if (g || encounterId === undefined || !ready || !held || read === encounterId || turn > 1) return;
    const t = setTimeout(() => setRead(encounterId), NO_STORY_MS);
    return () => clearTimeout(t);
  }, [g, encounterId, ready, held, read, turn]);
  const setPlaying = useCinematic((s) => s.setFaceoffPlaying);
  useEffect(() => { setPlaying(run !== undefined && run === encounterId); }, [run, encounterId, setPlaying]);
  useEffect(() => () => setPlaying(false), [setPlaying]);
  const leader = leaderUnitOf(snapshot, g);
  const tx = leader?.tile.x, ty = leader?.tile.y;
  const heroes = useMemo(() => Object.values(heroNames), [heroNames]);
  const onFocus = useCallback((on: boolean) => setFocus(on && tx !== undefined && ty !== undefined ? { x: tx, y: ty } : undefined), [setFocus, tx, ty]);
  const onRead = useCallback(() => setRead(encounterId), [encounterId]);
  const onDone = useCallback(() => { setRun(undefined); setFocus(undefined); setRead(encounterId); }, [setFocus, encounterId]);
  if (!g || run === undefined || run !== encounterId) return null;
  return <FaceoffView g={g} heroes={heroes} leaderFrame={leader?.appearance} onFocus={onFocus} onRead={onRead} onDone={onDone} stage />;
}

// ---- escape ---------------------------------------------------------------------------------------

type EscStep = 'getaway' | 'vow' | 'born';

/** The named-foe card as a pure view (live HUD and the `?escape=1` harness). */
export function EscapeView({ g, heroes, leaderFrame, onDone, start = 'getaway' }: { g: Genesis; heroes: string[]; leaderFrame?: number; onDone: () => void; start?: EscStep }) {
  const [step, setStep] = useState<EscStep>(start);
  const [typed, setTyped] = useState<EscStep>();
  const reduced = useReducedMotion();
  const n = g.battle.named_foe;
  const leader = display(g.battle.leader.name);
  const getaway = withHeroes(n?.getaway ?? `${leader} slips away.`, heroes);
  const vow = withHeroes(n?.vow ?? 'This is not over.', heroes);
  const next = useCallback(() => setStep((s) => (s === 'getaway' ? 'vow' : s === 'vow' ? 'born' : s)), []);
  const finish = useCallback(() => { if (step === 'born') onDone(); else setStep('born'); }, [step, onDone]);
  useEffect(() => {
    if (step !== 'born' && typed !== step) return;
    const ms = step === 'born' ? BORN_MS : readMs(step === 'vow' ? vow : getaway) + 400;
    const t = setTimeout(step === 'born' ? onDone : next, ms);
    return () => clearTimeout(t);
  }, [step, typed, vow, getaway, next, onDone]);
  useCinematicKeys(true, useCallback((k) => { if (k === 'cine.skip') finish(); else if (step === 'born') onDone(); else next(); }, [finish, next, step, onDone]));
  // docs/43: the getaway's beats (smoke, a splash, wings) and the vow's land as the words appear
  const [typedN, setTypedN] = useState(0);
  useEffect(() => { setTypedN(0); }, [step]);
  useBeatScope(step);
  useMusicMood(step === 'born' ? 'dread' : 'tension', true);
  // docs/44: the getaway sounds how they go; the vow slides in; the named foe is born on a braam
  useEffect(() => {
    if (step === 'getaway') playSfx('cine.escape', { band: n?.escape ? ESCAPE_BAND[n.escape] : 0 });
    else if (step === 'vow') playSfx('cine.line');
    else playSfx('cine.born');
  }, [step]);
  const beats = useMemo(() => (step === 'getaway' ? beatsFor(g, 'Getaway', 0, getaway) : step === 'vow' ? beatsFor(g, 'Vow', 0, vow) : []), [g, step, getaway, vow]);
  useBeats(beats, typedN);
  return createPortal(
    <div className={`fk-faceoff fk-escape ${reduced ? 'fk-faceoff--still' : ''}`} role="dialog" aria-modal="true" aria-label={`${leader} escapes`} data-testid="named-foe-escape" data-step={step} onClick={step === 'born' ? undefined : next}>
      <SightLayer fixed />
      <div className="fk-cine__bar fk-cine__bar--top">
        <span className="fk-cine__eyebrow">{g.battle.title} · the leader gets away</span>
        {step !== 'born' && <Button small variant="ghost" className="fk-cine__skip" onClick={(e) => { e.stopPropagation(); finish(); }} data-testid="named-foe-escape-skip">Skip <span aria-hidden="true">▸</span> <HotkeyKbd id="cine.skip" /></Button>}
      </div>
      <div className="fk-cine__bar fk-cine__bar--bottom" />
      {step === 'getaway' && <Narration key="getaway" eyebrow={`${leader} · ${n?.escape ? ESCAPE_WORD[n.escape] : 'gone'}`} text={getaway} onDone={() => setTyped('getaway')} onType={setTypedN} />}
      {step === 'vow' && (
        <div className="fk-cine__line" onClick={(e) => e.stopPropagation()}>
          <DialogueBox key="vow" data-testid="named-foe-vow" className="fk-cine__dialogue fk-cine__dialogue--cry" cps={50} typeGain={CINE_TYPE_GAIN}
            speaker={<>{leader}<span className="fk-cine__tag"> · the vow</span></>}
            portrait={<Portrait frame={leaderFrame} scale={3} title={leader} />}
            text={vow} more onDone={() => setTyped('vow')} onAdvance={next} onType={setTypedN} />
        </div>
      )}
      {step === 'born' && (
        <div className="fk-escape__born" data-testid="named-foe-born" onClick={(e) => e.stopPropagation()}>
          <span className="fk-escape__born-eyebrow">A named foe is born</span>
          <span className="fk-escape__born-face"><Portrait frame={leaderFrame} scale={4} glow title={leader} /></span>
          <span className="fk-escape__born-name">{leader}</span>
          <span className="fk-escape__born-title">{g.battle.leader.title}</span>
          <span className="fk-escape__born-note">They will remember this, and so will {g.realm.name || 'the realm'}.</span>
          <Button variant="primary" onClick={onDone} data-testid="named-foe-escape-continue" autoFocus>Continue</Button>
        </div>
      )}
    </div>,
    document.body,
  );
}

/** The opening's moments, decoded as the encounter opens so the face-off lands on time (docs/44). */
const MOMENTS = ['cine.rise', 'cine.name', 'cine.line', 'cine.cry', 'cine.objective', 'cine.escape', 'cine.born'] as const;

/** `cine.cry` bands (horn, strike, roar) and `cine.escape` bands, in the banks file's order. */
const CRY_BAND: Record<string, number> = { Warband: 0, Ambush: 1, Beast: 2 };
const ESCAPE_BAND: Record<string, number> = { Smoke: 0, Water: 1, Burrow: 2, Steed: 3, Wings: 4, Shadow: 5 };

const ESCAPE_WORD: Record<string, string> = { Smoke: 'gone in smoke', Water: 'gone under the water', Burrow: 'gone to ground', Steed: 'gone at a gallop', Wings: 'gone on the wind', Shadow: 'gone into shadow' };

/** A narrator's title card: an eyebrow and a typed line, centred between the bars. */
function Narration({ eyebrow, text, onDone, onType }: { eyebrow: ReactNode; text: string; onDone: () => void; onType?: (n: number) => void }) {
  return (
    <div className="fk-cine__narration" data-testid="named-foe-getaway">
      <span className="fk-cine__narration-eyebrow">{eyebrow}</span>
      <DialogueBox className="fk-cine__narration-box" text={text} cps={55} typeGain={CINE_TYPE_GAIN} onDone={onDone} onType={onType} />
    </div>
  );
}

/**
 * The live escape card: once the opening is won — the field secured (docs/25 D1: the leader drops
 * on the securing pulse, before the party explores), or, failing that row, the encounter ended in
 * victory — its last pulse has played out and the leader is off the board. The RouteDriver holds the route on the epilogue until this browser
 * has seen it (`useCinematic.escapeSeen`).
 */
export function NamedFoeEscape() {
  const encounterId = useEncounter((s) => s.encounterId);
  const g = useOpeningStory(encounterId);
  const state = useEncounter((s) => s.state);
  const outcome = useEncounter((s) => s.outcome);
  const secured = useEncounterSecured(encounterId);
  const snapshot = useEncounter((s) => s.snapshot);
  const caughtUp = useEncounter((s) => !!s.snapshot && s.boardSnapshot === s.snapshot);
  const playing = usePlayback((s) => s.playing);
  const heroNames = useEncounter((s) => s.heroNames);
  const seen = useCinematic((s) => s.escapeSeen);
  const markSeen = useCinematic((s) => s.setEscapeSeen);
  useCinematic((s) => s.rev);
  const heroes = useMemo(() => Object.values(heroNames), [heroNames]);
  const leader = leaderUnitOf(snapshot, g);
  // the last board may no longer carry the leader: keep the face we saw
  const frame = useRef<number | undefined>(undefined);
  if (leader?.appearance) frame.current = leader.appearance;
  const won = secured ? secured.outcome : state === ENC_ENDED ? outcome : undefined;
  const over = !!g && encounterId !== undefined && won !== undefined && WON.has(won) && caughtUp && !playing;
  // a secured field has no foes on it; a leader who yielded did not get away (Surrendered = 3)
  const gone = (!leader || leader.onBoard === false) && won !== 3;
  const pending = over && !seen(encounterId);
  // a win where the leader did not get away (they yielded): nothing to show, let the route go
  useEffect(() => { if (pending && !gone) markSeen(encounterId); }, [pending, gone, markSeen, encounterId]);
  const onDone = useCallback(() => { if (encounterId !== undefined) markSeen(encounterId); }, [markSeen, encounterId]);
  if (!pending || !gone || !g) return null;
  return <EscapeView g={g} heroes={heroes} leaderFrame={leader?.appearance ?? frame.current} onDone={onDone} />;
}
